import memorise from 'lru-memorise';
import type { Product } from './Product';
import type { PropertySpecification } from './Specification';
import type { I18NInstance } from '@/utils/createI18nInstance';
import type { ProductTranslations } from './config';
import { getFilterableAttributeValuesForAutomatic } from '@/widgets/ProductList/utils/cleanCollections';
import type { Categories } from './Category';

export type ConfigUniqueId = string;

export type FilterableValue = string | number | boolean;

const SUPERCATEGORY_KEY = 'superCategory';

export const FilterableAttributeKeysSchemaEnumForCapsules = [
  'cupSizes',
  'intensity',
  'isDecaffeinated',
  'collections',
  'aromatics',
  'inStock',
  SUPERCATEGORY_KEY
  // TODO: These will be useful for Machines
  // 'range',
  // 'model'
] as const;

export const FilterableAttributeKeysSchemaEnumForAccessories = [
  SUPERCATEGORY_KEY
] as const;

// TODO: Most of this should just have been translations - and the rest simple options
export type TechnologyFilterExtraInfo = {
  title: string;
  content: string;
  capsuleOptions: {
    type: string;
    image: string;
    features: {
      text: string;
      available: boolean;
    }[];
    buttonLabel: string;
    buttonHref: string;
  }[];
};

export type FilterableAttributeKey =
  | (typeof FilterableAttributeKeysSchemaEnumForCapsules)[number]
  | (typeof FilterableAttributeKeysSchemaEnumForAccessories)[number];

export type FilterableAttribute = {
  label: string;
  icon?: string;
};

export type FilterOption = {
  id: ConfigUniqueId;
  label: string;
  values: FilterableValue[];
  icon?: string;
  selected?: boolean;
  superCategory?: string;
};

export type Filter = {
  id: ConfigUniqueId;
  key: FilterableAttributeKey;
  label: string;
  options: FilterOption[];
  icon?: string;
  method?: 'AND' | 'OR';
  type?: 'AUTOMATIC' | 'QUICK' | 'MANUAL' | 'SUB-CATEGORY';
  parentCategoryName?: string;
};

export type FilterDefinition = {
  id: ConfigUniqueId;
  type: 'AUTOMATIC' | 'QUICK' | 'MANUAL' | 'SUB-CATEGORY'; // See src/pages/shared_res/agility/cms/schemas/widget_product-list.schema.json.ts:options/filters
  key: FilterableAttributeKey;
  label: string;
  icon?: string;
  options?: FilterDefinitionOption[];
  values?: FilterableValue[];
  method?: 'AND' | 'OR';
  parentCategoryName?: string; // Only used for superCategory
  order?: 'ALPHANUMERIC' | 'NATIVE'; // Only used for AUTOMATIC and SUB-CATEGORY
};

export type FilterDefinitionOption = {
  id: ConfigUniqueId;
  label: string;
  values: FilterableValue[];
  icon?: string;
};

export type SortableValue = string | number;

export const SortableAttributeKeysSchemaEnum = [
  'price',
  'intensity',
  'name'
] as const;

export type SortableAttributeKey =
  (typeof SortableAttributeKeysSchemaEnum)[number];

export type SortOption = {
  id: ConfigUniqueId;
  key: SortableAttributeKey;
  label: string;
  type: 'NUMERIC' | 'ALPHANUMERIC';
  order: 'ASCENDING' | 'DESCENDING';
};

export type SortOptionDefinition = SortOption;

export function createFilters(
  filterConfigs: FilterDefinition[],
  products: Product[],
  i18nextInstance: I18NInstance<ProductTranslations>,
  categories: Categories
): Filter[] {
  const { language: locale } = i18nextInstance;

  return filterConfigs
    .map((filterConfig) => {
      const splitToCategories =
        filterConfig.type === 'SUB-CATEGORY' ||
        filterConfig.key === SUPERCATEGORY_KEY;
      const key = splitToCategories ? SUPERCATEGORY_KEY : filterConfig.key;
      const filterableAttributeValues = findAllFilterableAttributeValues(
        key,
        products,
        i18nextInstance,
        filterConfig.parentCategoryName
      );

      switch (filterConfig.type) {
        case 'MANUAL':
          return {
            id: filterConfig.id,
            key,
            label: filterConfig.label,
            type: filterConfig.type,
            method: filterConfig.method,
            options: filterConfig.options
              ?.map((option) => ({
                id: option.id,
                label: option.label,
                icon: option.icon,
                values: option.values
              }))
              .filter((option) =>
                isValidOptionForCurrentProductList(
                  option,
                  filterableAttributeValues
                )
              )
          };

        case 'AUTOMATIC':
        case 'SUB-CATEGORY': {
          const sortByCategories = filterConfig.order === 'NATIVE';
          const categoriesWithIndex = new Map(
            sortByCategories
              ? Array.from(categories).map(([, category], index) => [
                  category.id,
                  index
                ])
              : []
          );
          const filterableAttributeValuesForAutomatic =
            getFilterableAttributeValuesForAutomatic(
              filterableAttributeValues,
              filterConfig.id,
              products
            );

          return {
            id: filterConfig.id,
            key,
            label: filterConfig.label,
            type: filterConfig.type,
            parentCategoryName: filterConfig.parentCategoryName,
            options: [...filterableAttributeValuesForAutomatic.entries()]
              .map(([value, { label, icon }]) => ({
                id: String(value).toLowerCase(),
                label,
                values: [value],
                icon,
                superCategory: splitToCategories ? value : undefined
              }))
              .sort((optionA, optionB) =>
                sortByCategories
                  ? (categoriesWithIndex.get(optionA.id) ?? 0) -
                    (categoriesWithIndex.get(optionB.id) ?? 0)
                  : optionA.label.localeCompare(optionB.label, locale)
              )
          };
        }

        case 'QUICK': {
          const option: FilterOption = {
            id: filterConfig.id,
            label: filterConfig.label,
            icon: filterConfig.icon,
            values: filterConfig.values ?? []
          };

          if (
            isValidOptionForCurrentProductList(
              option,
              filterableAttributeValues
            )
          ) {
            return {
              id: filterConfig.id,
              key,
              label: filterConfig.label,
              type: filterConfig.type,
              method: filterConfig.method,
              options: { ...option }
            };
          }
          return undefined;
        }

        default: {
          return undefined;
        }
      }
    })
    .filter((filter) => {
      if (!filter) return false;

      // Remove filters with no options
      if (Array.isArray(filter.options) && filter.options.length === 0)
        return false;

      return true;
    }) as Filter[];
}

export function createSortOptions(
  sortConfig: SortOptionDefinition[]
): SortOption[] {
  return sortConfig;
}

export const filterableAttributes = memorise(
  (
    product: Product,
    i18nextInstance: I18NInstance<ProductTranslations>,
    parentCategoryName?: string
  ): Record<
    FilterableAttributeKey,
    Map<FilterableValue, FilterableAttribute>
  > => {
    const { t } = i18nextInstance;

    const filterableAttributesOutput = {
      cupSizes: new Map(
        (
          (product.specifications.cupSizes as PropertySpecification[]) ?? []
        ).map((spec) => [
          spec.value,
          {
            label: spec.title,
            icon: spec.icon
          }
        ])
      ),
      intensity: new Map([
        [
          (product.specifications.intensity as PropertySpecification)?.value,
          {
            label: (product.specifications.intensity as PropertySpecification)
              ?.title,
            icon: (product.specifications.intensity as PropertySpecification)
              ?.icon
          }
        ]
      ]),
      isDecaffeinated: new Map([
        [
          (product.specifications.decaf as PropertySpecification)?.value,
          {
            label: (product.specifications.decaf as PropertySpecification)
              ?.title,
            icon: (product.specifications.decaf as PropertySpecification)?.icon
          }
        ]
      ]),
      collections: new Map(
        (
          (product.specifications.collections as PropertySpecification[]) ?? []
        ).map((spec) => [
          spec.value,
          {
            label: spec.title
          }
        ])
      ),
      aromatics: new Map(
        (
          (product.specifications.aromatics as PropertySpecification[]) ?? []
        ).map((spec) => [
          spec.value,
          {
            label: spec.title
          }
        ])
      ),
      inStock: new Map([
        [product.inStock, { label: t('product.property.inStock') }]
      ]),
      superCategory: new Map(
        Array.from(product.categories.values())
          .filter((category) =>
            category?.code?.includes(parentCategoryName ?? '')
          )
          .map((category) => [category.code, { label: category.name }])
      )
    };

    return filterableAttributesOutput;
  },
  {
    cacheKeyResolver: (product, i18n, parentCategoryName?: string) =>
      product.id + parentCategoryName
  }
);

export const sortableAttributes = memorise(
  (product: Product): Record<SortableAttributeKey, SortableValue> => ({
    price: product.pricing.price,
    name: product.name,
    intensity: (product.specifications.intensity as PropertySpecification)
      ?.value as number
  }),

  { cacheKeyResolver: (product) => product.id }
);

function findAllFilterableAttributeValues(
  key: FilterableAttributeKey,
  products: Product[],
  i18nextInstance: I18NInstance<ProductTranslations>,
  parentCategoryName?: string
): Map<FilterableValue, FilterableAttribute> {
  let attributes = new Map<FilterableValue, FilterableAttribute>();
  for (const product of products) {
    const attribute = filterableAttributes(
      product,
      i18nextInstance,
      parentCategoryName
    )[key];
    if (attribute) {
      attributes = new Map([...attributes, ...attribute]);
    }
  }

  return attributes;
}

function isValidOptionForCurrentProductList(
  option: FilterOption,
  filterableAttributeValues: Map<FilterableValue, FilterableAttribute>
): boolean {
  return option.values.some((value) => filterableAttributeValues.has(value));
}
